/**
 * Results scene — full survey board.
 *
 * Lives between questions; the FSM holds it for ~4s before moving to the
 * next prompt. Shows ALL surveyed answers (matched and unmatched) with
 * per-row points + matched-count chips. The previous "scorers this round"
 * panel was removed — names live on the right-side leaderboard, the
 * canonical place for them.
 *
 * The whole panel is constrained to MAX_W and pinned to the left of the
 * stage so the right-side leaderboard panel stays untouched.
 */

import type { QuestionResults } from '../state/types.js';

export interface ResultsHandle {
  el: HTMLDivElement;
  setResults(r: QuestionResults): void;
  setVisible(v: boolean): void;
}

const MAX_W = 1180;

export function createResults(stage: HTMLElement): ResultsHandle {
  const el = document.createElement('div');
  el.setAttribute('data-scene', 'results');
  el.setAttribute('data-testid', 'ss-results');
  el.style.cssText = `
    position: absolute; inset: 0;
    padding: 60px 60px;
    color: var(--sg-text-on-secondary, #FFC43D);
    font-family: 'Oswald', sans-serif;
    pointer-events: none;
  `;

  const inner = document.createElement('div');
  inner.setAttribute('data-testid', 'ss-results-inner');
  inner.style.cssText = `
    width: 100%;
    max-width: ${MAX_W}px;
    height: 100%;
    background: rgba(6,12,233,0.55);
    border: 3px solid var(--sg-primary, #FFC43D);
    border-radius: 18px;
    padding: 24px 28px;
    overflow: hidden;
    display: flex; flex-direction: column;
    gap: 8px;
  `;

  const title = document.createElement('div');
  title.setAttribute('data-testid', 'ss-results-title');
  title.style.cssText = `
    font-family: 'Bebas Neue', sans-serif;
    font-size: 56px; letter-spacing: 4px;
    color: var(--sg-primary, #FFC43D);
  `;
  title.textContent = 'SURVEY SAID';

  const list = document.createElement('div');
  list.setAttribute('data-testid', 'ss-results-list');
  list.style.cssText = `
    margin-top: 8px;
    flex: 1;
    display: flex; flex-direction: column;
    gap: 8px;
    overflow: hidden;
  `;

  const total = document.createElement('div');
  total.setAttribute('data-testid', 'ss-results-total');
  total.style.cssText = `
    margin-top: 14px;
    padding-top: 10px;
    border-top: 1px solid rgba(255,196,61,0.4);
    font-size: 24px;
    display: flex; justify-content: space-between;
  `;

  inner.appendChild(title);
  inner.appendChild(list);
  inner.appendChild(total);
  el.appendChild(inner);
  stage.appendChild(el);

  return {
    el,
    setResults(r) {
      // Title shows "Q N/M — SURVEY SAID" when capped, or just "SURVEY SAID"
      // in continuous-play mode (`total === 0`).
      title.textContent = r.total > 0
        ? `Q${r.index + 1}/${r.total} — SURVEY SAID`
        : `Q${r.index + 1} — SURVEY SAID`;

      list.innerHTML = '';
      if (r.board.length === 0) {
        // Empty board only happens for a corpus-error edge case (survey with
        // no answers). Show a friendly placard rather than 0 rows.
        const placard = document.createElement('div');
        placard.setAttribute('data-testid', 'ss-results-empty');
        placard.style.cssText = 'opacity: 0.85; font-size: 26px; line-height: 1.4; padding: 8px 0;';
        placard.textContent = 'No surveyed answers were found.';
        list.appendChild(placard);
      } else {
        for (const b of r.board) {
          list.appendChild(buildRow(b));
        }
      }

      total.innerHTML = '';
      const totalLbl = document.createElement('span');
      totalLbl.textContent = 'Total awarded';
      totalLbl.style.cssText = 'opacity: 0.85;';
      const totalVal = document.createElement('span');
      totalVal.textContent = `+${r.totalPointsAwarded}`;
      totalVal.style.cssText = 'color: var(--sg-celebration, #22C55E); font-weight: 700;';
      total.appendChild(totalLbl);
      total.appendChild(totalVal);
    },
    setVisible(v) {
      el.style.display = v ? 'flex' : 'none';
    },
  };
}

function buildRow(b: {
  rank: number;
  answer: string;
  count: number;
  points: number;
  matched_count: number;
  was_matched: boolean;
}): HTMLDivElement {
  const matched = b.was_matched;
  const row = document.createElement('div');
  row.setAttribute('data-testid', `ss-results-row-${b.rank}`);
  row.setAttribute('data-matched', matched ? '1' : '0');
  row.style.cssText = `
    display: grid;
    grid-template-columns: 36px 1fr 80px 110px 130px;
    align-items: center;
    column-gap: 14px;
    font-size: 26px;
    opacity: ${matched ? 1 : 0.7};
  `;

  const rk = document.createElement('span');
  rk.style.cssText = 'color: var(--sg-primary, #FFC43D); font-family: "Bebas Neue", sans-serif; font-size: 28px; text-align: center;';
  rk.textContent = String(b.rank);

  const ans = document.createElement('span');
  ans.style.cssText = 'overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-transform: uppercase; letter-spacing: 1px;';
  ans.textContent = b.answer;

  const pct = document.createElement('span');
  pct.style.cssText = 'text-align: right; color: rgba(255,255,255,0.92); font-family: "Bebas Neue", sans-serif; font-size: 24px;';
  pct.textContent = `${b.count}%`;

  const pts = document.createElement('span');
  pts.style.cssText = `text-align: right; color: var(--sg-celebration, #22C55E); font-weight: 700; opacity: ${matched ? 1 : 0.55};`;
  pts.textContent = `+${b.points} pts`;

  const matchedChip = document.createElement('span');
  matchedChip.style.cssText = `
    justify-self: end;
    background: ${matched ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.18)'};
    color: ${matched ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.55)'};
    border-radius: 999px;
    padding: 3px 12px;
    font-size: 20px;
    letter-spacing: 0.5px;
  `;
  matchedChip.textContent = `${b.matched_count} matched`;

  row.appendChild(rk);
  row.appendChild(ans);
  row.appendChild(pct);
  row.appendChild(pts);
  row.appendChild(matchedChip);
  return row;
}
